import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Avatar,
  Typography,
  Alert,
  Box
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { adminApi, persistAdminUserFromPayload } from 'src/utils/adminApi';

function profileImageUrl(user) {
  return user?.profileImage || user?.staff_member?.avatar || '';
}

export default function AdminProfileDialog({ open, onClose, user, onUpdated }) {
  const fileRef = useRef(null);
  const [name, setName] = useState(user?.name || '');
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState(profileImageUrl(user));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleOpen = () => {
    setName(user?.name || '');
    setPendingFile(null);
    setPreview(profileImageUrl(user));
    setError('');
  };

  const onPickImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/') && !/\.(png|jpe?g|webp)$/i.test(file.name || '')) {
      setError('Please choose a PNG, JPG, or WEBP image.');
      return;
    }
    setError('');
    setPendingFile(file);
    setPreview(URL.createObjectURL(file));
    event.target.value = '';
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      let latest = user;
      const trimmed = name.trim();
      if (trimmed && trimmed !== user?.name) {
        const result = await adminApi.updateMyProfile(trimmed);
        latest = persistAdminUserFromPayload(result) || latest;
      }
      if (pendingFile) {
        const result = await adminApi.updateMyProfileImage(pendingFile);
        latest = persistAdminUserFromPayload(result) || latest;
      }
      onUpdated?.(latest);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not update profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} TransitionProps={{ onEnter: handleOpen }} fullWidth maxWidth="xs">
      <DialogTitle>Edit profile</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Box
            onClick={() => fileRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileRef.current?.click();
              }
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 1,
              px: 2,
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <Avatar src={preview} alt={name || 'Profile'} sx={{ width: 72, height: 72 }} />
            <Typography variant="caption" color="text.secondary">
              Click to change photo
            </Typography>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} />
          </Box>
          <TextField
            label="Display name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} disabled={loading}>
          Cancel
        </LoadingButton>
        <LoadingButton variant="contained" loading={loading} onClick={handleSave}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

AdminProfileDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  user: PropTypes.object,
  onUpdated: PropTypes.func
};
